import {onScopeDispose} from 'vue';

// What counts as seen (IPD-2974 8.2): half the element, held for a full second without leaving.
// Anything shorter is a tile that swept past under a fast scroll, which is the case the
// measurement exists to tell apart from a tile someone looked at.
const SEEN_FRACTION = 0.5;
const DWELL_MS = 1000;

// A stepped list rather than the fraction alone. `intersectionRatio` is a share of the ELEMENT, so
// an element taller than twice the viewport can never reach half of itself at any scroll position:
// with a single 0.5 threshold the observer would deliver the one initial entry and then nothing,
// and the tile would never be reported however long it was read. Not hypothetical here — the
// programs catalogue draws a row per program with no cap and the journal grows with its entries,
// both at the bottom of the page, which is exactly where the measurement is meant to look. With
// steps the entries keep arriving, and `isSeen` decides on heights instead of the ratio.
const THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];

// Intersection rects are quantised to sub-pixels, so the entry delivered for crossing a boundary
// can carry a hair less than the boundary itself. Without the slack a tile that comes to rest at
// exactly half visible has its only entry rejected and never gets another, because nothing crosses
// again while it sits still.
const EPSILON_PX = 1;

/**
 * Half the tile, or half the screen if the tile is bigger than that.
 *
 * The second half of the rule is what makes a tall tile measurable at all: filling half the
 * viewport is as much as it can ever do, and it is at least as strong a claim as showing half of a
 * short one.
 *
 * Heights only, so a tile clipped at the side counts as seen. Right for the bento, whose cells are
 * never off to one side, and worth knowing before this is pointed at anything that scrolls across.
 */
function isSeen(entry: IntersectionObserverEntry): boolean {
  const tileHeight = entry.boundingClientRect.height;
  const visibleHeight = entry.intersectionRect.height;
  if (!entry.isIntersecting || tileHeight <= 0 || visibleHeight <= 0) {
    return false;
  }
  const halfTile = tileHeight * SEEN_FRACTION;
  const halfView = (entry.rootBounds?.height ?? 0) * SEEN_FRACTION;
  return visibleHeight + EPSILON_PX >= (halfView > 0 ? Math.min(halfTile, halfView) : halfTile);
}

/** Registers elements to be reported the first time each one is properly seen. */
export interface InViewOnceTracker {
  watch(element: Element | null | undefined): void;
}

/**
 * Reports each watched element once, the first time it is properly on screen for [DWELL_MS] with
 * the tab in front.
 *
 * One observer for every element rather than one each: the caller is a grid registering all its
 * cells at mount, and a browser coalesces a shared observer's entries into a single callback.
 *
 * The dwell is a timer armed on the way in and disarmed on the way out, so scrolling a tile past
 * the fold and back arms it again from zero. Once an element reports it is unobserved, and a weak
 * set of what has already reported guards the case that would otherwise break "once": a template
 * function ref runs again on every re-render, so the caller hands the same element back whenever
 * its data changes.
 *
 * Timers do not stop for a tab nobody is looking at — a hidden document is throttled to a second,
 * which is exactly the dwell — while a hidden document also produces no entries, so nothing
 * arrives to cancel them. Left alone, opening the page in a background tab would report every tile
 * above the fold as seen, which is the opposite of what the dwell is for. So arming waits for the
 * tab to be in front, and what is on screen is tracked apart from what is counting down: that is
 * what lets the wait start again on return, instead of needing an entry the observer will not
 * send. Start again, not carry on — coming back re-arms a whole [DWELL_MS], which is the reading
 * the tests pin and the honest one, since nobody was looking at the tab in between.
 *
 * Guarded on the observer existing at all, the way `journal-card-stack.vue` guards its resize one:
 * the unit environment has none, and a page that silently measures nothing is the right failure
 * for analytics — never a crash on the page it is only observing.
 */
export function useInViewOnce(onSeen: (element: Element) => void): InViewOnceTracker {
  const dwelling = new Map<Element, ReturnType<typeof setTimeout>>();
  // On screen right now, whether or not it is counting down. The two differ while the tab is away.
  const onScreen = new Set<Element>();
  const reported = new WeakSet<Element>();
  let observer: IntersectionObserver | null = null;

  function pageIsInFront(): boolean {
    return document.visibilityState === 'visible';
  }

  function stopDwelling(element: Element): void {
    if (!dwelling.has(element)) {
      return;
    }
    clearTimeout(dwelling.get(element));
    dwelling.delete(element);
  }

  function report(element: Element): void {
    dwelling.delete(element);
    onScreen.delete(element);
    reported.add(element);
    observer?.unobserve(element);
    // A cell the grid dropped mid-dwell is not a tile anyone finished looking at. Marking it
    // reported first is what stops it being watched or counted once it has gone.
    if (element.isConnected) {
      onSeen(element);
    }
  }

  function arm(element: Element): void {
    if (reported.has(element) || dwelling.has(element) || !pageIsInFront()) {
      return;
    }
    dwelling.set(element, setTimeout(() => { report(element); }, DWELL_MS));
  }

  function handle(entries: IntersectionObserverEntry[]): void {
    entries.forEach((entry) => {
      if (!isSeen(entry)) {
        onScreen.delete(entry.target);
        stopDwelling(entry.target);
        return;
      }
      onScreen.add(entry.target);
      arm(entry.target);
    });
  }

  function onVisibilityChange(): void {
    if (pageIsInFront()) {
      onScreen.forEach(arm);
      return;
    }
    dwelling.forEach(clearTimeout);
    dwelling.clear();
  }

  // Cleans up with its own scope rather than relying on the caller to remember, the way
  // `use-intersection-loader` does with its observer. It also means this works anywhere an effect
  // scope does, not only inside a component's setup.
  onScopeDispose(() => {
    dwelling.forEach(clearTimeout);
    dwelling.clear();
    onScreen.clear();
    observer?.disconnect();
    observer = null;
    document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  return {
    watch(element: Element | null | undefined): void {
      if (!element || reported.has(element) || typeof IntersectionObserver === 'undefined') {
        return;
      }
      if (!observer) {
        observer = new IntersectionObserver(handle, {threshold: THRESHOLDS});
        document.addEventListener('visibilitychange', onVisibilityChange);
      }
      observer.observe(element);
    },
  };
}
